import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'components/Store';
import DetailPanel from 'components/RecordsListView/DetailPanel';
import DetailOverview from 'screens/Applications&WorkOrders/Components/DetailOverview';
import DetailsTabs from 'screens/Applications&WorkOrders/Components/DetailsTabs';
import useOperationProducts from 'screens/Applications&WorkOrders/Components/hooks/useOperationProducts';
import useOperationProperties from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import { getTargetPests } from 'screens/Applications&WorkOrders/Components/helpers';
import ActionMenu from 'screens/Applications&WorkOrders/Components/ActionMenu';
import { OperationType } from 'components/RecordsListView/constants';
import useApplicationDetails from 'screens/Applications&WorkOrders/Applications/Hooks/useApplicationDetails';
import useWorkOrderDetails from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrderDetails';
import { useSummaryParams } from 'components/RecordsListView/context/SummaryParamsContext';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import { mapChildrenRecords } from 'components/RecordsCreatedIndicators/helpers';
import { ChildrenRecords } from 'components/RecordsCreatedIndicators/types';
import useLinkedRecords from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/LinkedRecordsTab/hooks/useLinkedRecords';
import { LINKED_RECORDS_PAGE_SIZE } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/LinkedRecordsTab/LinkedRecordsTable';
import { LinkedRecordsState } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/LinkedRecordsTab/types';
import { TAB_KEYS } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';

export interface OperationDetailPanelProps {
  operationId: string | null;
  operationType: OperationType;
  isLoading: boolean;
  onOperationDeleted?: (deletedId: string) => void;
  hasOperations: boolean;
  summaryChildrenRecords?: ChildrenRecords;
}

interface PanelStateInput {
  isLoading: boolean;
  isLoadingDetails: boolean;
  hasOperations: boolean;
  operationId: string | null;
  operationDetails: OperationDetails | undefined;
  detailsError: unknown;
}

// Two windows would otherwise read as "no results": between a row being
// selected and its details arriving, and between the list finishing loading
// and its auto-selected first row landing (the list selects it in an effect,
// one render later). Both are still loading.
const resolvePanelState = ({
  isLoading,
  isLoadingDetails,
  hasOperations,
  operationId,
  operationDetails,
  detailsError
}: PanelStateInput) => {
  const isWaitingForDetails =
    !!operationId && operationId !== operationDetails?.id && !detailsError;
  const isAwaitingSelection = hasOperations && !operationId;

  return {
    isLoading:
      isLoading ||
      isLoadingDetails ||
      isWaitingForDetails ||
      isAwaitingSelection,
    isEmpty:
      !isWaitingForDetails && !isAwaitingSelection && !operationDetails?.id
  };
};

const OperationDetailPanel = ({
  operationId,
  operationType,
  isLoading,
  onOperationDeleted,
  hasOperations,
  summaryChildrenRecords
}: OperationDetailPanelProps) => {
  const {
    state: { organization }
  } = useStore();
  const { hasSearchTerm, hasFilters } = useSummaryParams();

  /*
    Pass null when not needed to avoid unnecessary API calls,
    Prevent fetching details when the operation summary is still loading
  */
  const applicationDetailsResult = useApplicationDetails(
    operationType === OperationType.APPLICATION && !isLoading
      ? operationId
      : null
  );
  const workOrderDetailsResult = useWorkOrderDetails(
    operationType === OperationType.WORK_ORDER && !isLoading
      ? operationId
      : null
  );

  // Get the appropriate details based on operation type
  const operationDetails: OperationDetails | undefined =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.applicationDetails
      : workOrderDetailsResult.workOrderDetails;

  const isLoadingDetails =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.isLoading
      : workOrderDetailsResult.isLoading;

  const detailsError =
    operationType === OperationType.APPLICATION
      ? applicationDetailsResult.errors
      : workOrderDetailsResult.errors;

  // Only fetch products/properties if the operation belongs to the current organization
  const isOperationFromCurrentOrg =
    operationDetails?.orgId === organization?.id;

  const operationIdForHooks = isOperationFromCurrentOrg
    ? operationDetails?.id
    : null;

  const operationTypeString =
    operationType === OperationType.APPLICATION ? 'application' : 'work-order';

  const { products, isLoading: isLoadingProducts } = useOperationProducts(
    operationIdForHooks,
    operationTypeString
  );

  const {
    propertiesData,
    isLoading: isLoadingProperties
  } = useOperationProperties(operationIdForHooks, operationTypeString);

  const operationDetailsWithTargetPests = useMemo(() => {
    if (!operationDetails) return operationDetails;

    // The same productId can appear multiple times in operationDetails (e.g. split
    // across crop zones), so we group all entries by productId into ordered lists.
    const productDetailsByProductId = new Map<
      string,
      NonNullable<typeof operationDetails.products>[number][]
    >();
    operationDetails.products?.forEach(product => {
      const list = productDetailsByProductId.get(product.productId) ?? [];
      list.push(product);
      productDetailsByProductId.set(product.productId, list);
    });

    const consumedIndices = new Map<string, number>();

    return {
      ...operationDetails,
      products: products?.map(product => {
        const list = productDetailsByProductId.get(product.productId) ?? [];
        // Each time we encounter a productId we advance its pointer so that
        // repeated entries in `products` consume distinct detail records in order.
        const index = consumedIndices.get(product.productId) ?? 0;
        // Fall back to list[0] if there are fewer detail records than product rows.
        const productDetail = list[index] ?? list[0];
        consumedIndices.set(product.productId, index + 1);

        return {
          ...productDetail,
          ...product,
          targetPests: getTargetPests(product, products)
        };
      })
    };
  }, [operationDetails, products]);

  const testId =
    operationType === OperationType.APPLICATION
      ? 'application-detail-panel'
      : 'work-order-detail-panel';

  const showWeatherTab = operationType === OperationType.APPLICATION;

  /**
   * Summed across every child type: the endpoint returns all of them, so
   * counting one would let the label disagree with its own rows.
   */
  const linkedRecordsCount = useMemo(() => {
    if (operationType !== OperationType.WORK_ORDER) {
      return 0;
    }
    const records = summaryChildrenRecords ?? operationDetails?.childrenRecords;
    return Object.values(mapChildrenRecords(records)).reduce(
      (total, count) => total + (count ?? 0),
      0
    );
  }, [summaryChildrenRecords, operationDetails, operationType]);

  const [linkedRecordsPage, setLinkedRecordsPage] = useState(0);
  const [activeDetailsTab, setActiveDetailsTab] = useState<string>();

  useEffect(() => {
    setLinkedRecordsPage(0);
  }, [operationId]);

  // Null until the Linked Records tab is actually selected keeps the request
  // from firing just because the panel opened.
  const {
    linkedRecords: linkedRecordsResponse,
    isLoading: isLoadingLinkedRecords,
    errors: linkedRecordsErrors
  } = useLinkedRecords(
    operationType === OperationType.WORK_ORDER &&
      linkedRecordsCount > 0 &&
      activeDetailsTab === TAB_KEYS.LINKED_RECORDS
      ? operationId
      : null,
    linkedRecordsPage,
    LINKED_RECORDS_PAGE_SIZE
  );

  const linkedRecords: LinkedRecordsState = useMemo(
    () => ({
      records: linkedRecordsResponse?.results ?? [],
      totalRecords: linkedRecordsResponse?.totalElements ?? 0,
      isLoading: isLoadingLinkedRecords,
      hasError: !!linkedRecordsErrors,
      page: linkedRecordsPage,
      onPageChange: setLinkedRecordsPage
    }),
    [
      linkedRecordsResponse,
      isLoadingLinkedRecords,
      linkedRecordsErrors,
      linkedRecordsPage
    ]
  );

  const panelState = resolvePanelState({
    isLoading,
    isLoadingDetails,
    hasOperations,
    operationId,
    operationDetails,
    detailsError
  });

  return (
    <DetailPanel
      title={operationDetails?.name}
      isLoading={panelState.isLoading}
      isEmpty={panelState.isEmpty}
      showSearchEmptyState={hasSearchTerm && !hasOperations}
      showFilterEmptyState={hasFilters && !hasOperations}
      operationType={operationType}
      testId={testId}
      actionMenu={
        <ActionMenu
          operation={operationDetailsWithTargetPests}
          operationType={operationType}
          onDelete={onOperationDeleted}
        />
      }
    >
      <DetailOverview details={operationDetails} />
      <DetailsTabs
        details={operationDetails}
        products={products}
        propertiesData={propertiesData}
        tankInformation={operationDetails?.tankInformation}
        isLoadingProducts={isLoadingProducts}
        isLoadingProperties={isLoadingProperties}
        showWeatherTab={showWeatherTab}
        linkedRecordsCount={linkedRecordsCount}
        workOrderId={operationId ?? undefined}
        linkedRecords={linkedRecords}
        onActiveTabChange={setActiveDetailsTab}
        type={
          operationType === OperationType.WORK_ORDER
            ? OperationType.WORK_ORDER
            : undefined
        }
      />
    </DetailPanel>
  );
};

export default OperationDetailPanel;
